import { AppImages, I18N, navigation, React, variantProvider } from '@/app'
import { Alert, OverlayLessonLearn, Page, PageGradientTop, Pager, View } from '@/components'
import { Learn } from '@/features'
import { analytics, APIClient } from '@/services'
import { locationPath } from '@/utils'
import { useModal } from '@codeleap/common'
import { TypeGuards } from '@codeleap/types'
import { navigate } from 'gatsby'
import { LessonLeave } from '../atoms/LessonLeave'
import { ModalLessonDone } from '../modals'

export function LessonCarousel() {
  const chapter = Learn.S.lesson.use(s => s.chapter)
  const moduleId = Learn.S.lesson.use(s => s.module?.id)

  const { inSwipe, pages, initialPage, origin } = Learn.useLessonContext()

  const achievements = APIClient.Achievements.useAchievements()

  const alert = useModal()

  const [lockPage, setLockPage] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(initialPage)

  const pagerRef = React.useRef(null)
  const hasSetInitialPage = React.useRef(false)
  const swipeLockTimeout = React.useRef<ReturnType<typeof setTimeout>>(null)
  const focusedPage = pages?.[currentPage]
  const hasOverlay = focusedPage?.web_foreground && focusedPage?.image_alt_text && !focusedPage?.has_translated_image

  const clearSwipeLock = () => {
    inSwipe.current = false

    if (swipeLockTimeout.current) {
      clearTimeout(swipeLockTimeout.current)
      swipeLockTimeout.current = null
    }
  }

  React.useEffect(() => {
    clearSwipeLock()
  }, [currentPage])

  const onBack = (root = false) => {
    Learn.S.learn.getState().clear()

    const returned = locationPath.returnToPath()

    if (returned) return

    if (root) {
      if (origin === 'onboarding_lesson') {
        navigation.navigate('Feed.Feed')
      } else {
        navigate(`/learn/` as never)
      }
    } else {
      navigate(`/learn/chapter/${moduleId}` as never)
    }
  }

  const handleCloseLesson = () => {
    alert.toggle()
  }

  const goToNextPage = (_nextPage: number = null) => {
    const swiped = TypeGuards.isNumber(_nextPage)
    const id = pages?.[currentPage]?.id

    let nextPage = swiped ? _nextPage : currentPage + 1

    let back = false

    if (!!id) {
      if (swiped) {
        analytics.track('lesson_swipe', { lesson_name: chapter?.title || '' })
        let pagesToRead = pages?.slice(currentPage, _nextPage)

        const pageToReadWithQuestion = pagesToRead?.find?.(p => TypeGuards.isNumber(p?.question))

        const choices = Learn.S.learn.getState().choices

        if (!!pageToReadWithQuestion?.id && !choices?.[pageToReadWithQuestion?.question as any]) {
          const pageWithQuestionIndex = pages?.findIndex?.(p => p?.id == pageToReadWithQuestion?.id)

          pagesToRead = pages.slice(currentPage, pageWithQuestionIndex)

          nextPage = pageWithQuestionIndex

          back = true
          setLockPage(true)
        }

        const pagesIds = pagesToRead?.map?.(p => p?.id)
        if (APIClient.Session.isLoggedIn()) {
          pagesIds?.forEach?.(pId => {
            if (!!pId) APIClient.Learn.readPage(pId) // TODO read pages per group
          })
        }
      } else {
        if (APIClient.Session.isLoggedIn()) {
          APIClient.Learn.readPage(id)
        }
      }
    }

    if (nextPage >= pages?.length) {
      handleLessonComplete()
      return
    }

    setCurrentPage(nextPage)

    if (back) {
      setTimeout(() => {
        pagerRef.current?.slickGoTo?.(nextPage)
        setLockPage(false)
      }, 600)
    }
  }

  const gotToPreviousPage = () => {
    const previousPage = currentPage - 1

    if (previousPage < 0) return

    setCurrentPage(previousPage)
  }

  const handleLessonComplete = () => {
    analytics.track('lesson_complete')
    Learn.updateLessonsOpenedCount({ ...chapter, done: true })
    APIClient.Learn.verifyCollectionAchievementProgress(achievements.items)
    if (!ModalLessonDone.isVisible) ModalLessonDone.open({ origin })
  }

  const onLeaveLesson = () => {
    APIClient.Learn.verifyCollectionAchievementProgress(achievements?.items)
    onBack(true)
  }

  const setPage = (_page: number) => {
    const next = _page > currentPage

    if (next) {
      goToNextPage(_page)
    } else {
      setCurrentPage(_page)
    }

    clearSwipeLock()
  }

  // const onShare = () => shareLessonProgress(chapter?.data)

  // const handleContinue = async () => {
  //   if (APIClient.Session.isLoggedIn()) {
  //     await handleOnboardingValidations(false)
  //   }
  // }

  return (
    <>
      <Page
        showHeaderImage={false}
        showBackgroundImage
        centerContent
        showSubHeader={false}
        showFooter={false}
        showHeader={false}
        pageTitle={chapter?.title}
        backgroundImageSource={focusedPage?.web_background ?? AppImages.OriginalGradient}
        styles={{
          wrapper: styles.page
        }}
      >
        {lockPage ? <View style={styles.overlay} /> : null}

        <PageGradientTop />

        {hasOverlay && (
          <View style={styles.translationOverlay}>
            <OverlayLessonLearn
              image_alt_text={focusedPage.image_alt_text}
              isRtl={focusedPage.is_rtl}
              has_translated_image={focusedPage.has_translated_image}
            />
          </View>
        )}

        <Learn.LessonViewHeader
          pages={pages?.length}
          currentPage={currentPage}
          text={chapter?.title ?? 'loading...'}
          chapterId={chapter?.id}
          onLeaveLesson={handleCloseLesson}
        />

        <View style={styles.pager}>
          <Pager
            debugName="LessonView Pager"
            disableSwipe={false}
            dots={false}
            page={currentPage}
            onChange={setPage}
            ref={pagerRef}
            beforeChange={() => {
              if (!hasSetInitialPage.current) {
                hasSetInitialPage.current = true
                return
              }

              if (!inSwipe.current) {
                inSwipe.current = true
              }

              if (swipeLockTimeout.current) clearTimeout(swipeLockTimeout.current)

              swipeLockTimeout.current = setTimeout(clearSwipeLock, 700)
            }}
          >
            {pages?.map((page, i) => {
              const pageProps = {
                page,
                goToNextPage,
                goToPreviousPage: gotToPreviousPage,
                visible: currentPage === i,
                isChapterDone: chapter?.done,
                index: i,
                currentPage: currentPage
              }

              if (i == 0) {
                return (
                  <Learn.LessonPage key={'lesson-page-back' + page?.id + i} {...pageProps}>
                    <LessonLeave onNext={goToNextPage} onLeave={onLeaveLesson} />
                  </Learn.LessonPage>
                )
              }

              return <Learn.LessonPage key={'lesson-page-' + page?.id} {...pageProps} />
            })}
          </Pager>
        </View>
      </Page>

      <Alert
        {...alert}
        debugName="Lesson view alert"
        title={I18N.t('learn.components.closeLesson.title')}
        body={I18N.t('learn.components.closeLesson.body')}
        type="destructive"
        onAction={onLeaveLesson}
        actionButtonProps={{
          text: I18N.t('learn.components.closeLesson.close'),
          debugName: 'close lesson view'
        }}
      />
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  t => ({
    page: {
      minHeight: '100svh',
      maxHeight: '100svh'
    },
    pager: {
      zIndex: 0,
      overflow: 'hidden',
      ...t.presets.absolute,
      ...t.presets.whole,
      height: '100svh',
      width: '100vw',
      ...t.presets.center,
      ...t.presets.row,
      background: t.colors.transparent
    },
    overlay: {
      width: '100vw',
      height: '100vh',
      background: t.colors.transparent,
      ...t.presets.absolute,
      ...t.presets.whole,
      zIndex: 10
    },
    translationOverlay: {
      position: 'fixed',
      bottom: t.spacing.value(12),
      left: t.spacing.value(2),
      zIndex: 5,
      width: 'fit-content'
    }
  }),
  true
)
